import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useMemo } from 'react';
import {
  AddCustomMetrics,
  AddTeams,
  Contributors,
  CustomMetrics,
  Dora,
  EditCustomMetrics,
  Feedback,
  IntegrationDetails,
  IntegrationGitDetails,
  IntegrationSettings,
  InvestmentCategories,
  Members,
  Notification,
  Organisation,
  SDLCMappings,
  SubsAndBilling,
  Survey,
  SurveyBuilder,
  SurveyEditor,
  Teams,
  ActiveOrganizations,
} from './asyncPages';
import NotFound from '../Status/404NotFound';
import { Card, Center, Stack, Text } from '@chakra-ui/react';
import { LockIcon } from '@devd-client/devd/components';
import { AddMetric } from '../Dashboard/asyncPages';
import { getOrgId, getRole, getUserId, UserRole } from '@devd-client/api';

// Wrapper component to extract surveyId from URL params
const SurveyEditorWrapper = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  return <SurveyEditor surveyId={surveyId} />;
};

const SurveyViewWrapper = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  return <SurveyEditor surveyId={surveyId} readOnly={true} />;
};

export type RouteType = {
  id: string;
  path: string;
  label: string;
  element: JSX.Element;
};

export function Settings() {
  const location = useLocation();

  const disableRoute = (route: RouteType) => {
    if (
      (route.id === 'SDLCMap' && getOrgId() !== 'SYMYCOXHO') ||
      (route.id === 'ActiveOrganizations' && getOrgId() !== 'OIDPPE')
    ) {
      return true;
    }
    return false;
  };

  const routes: RouteType[] = useMemo(() => {
    return [
      {
        id: 'Integrations',
        label: 'Integrations',
        path: 'integrations',
        element: <IntegrationSettings />,
      },
      {
        id: 'InvestmentCategories',
        label: 'Investment categories',
        path: 'investment-categories',
        element: <InvestmentCategories />,
      },
      {
        id: 'Teams',
        label: 'Teams',
        path: 'teams',
        element: <Teams />,
      },
      {
        id: 'members',
        label: 'members',
        path: 'members',
        element: <Members />,
      },
      {
        id: 'Dora',
        label: 'Dora',
        path: 'dora',
        element: <Dora />,
      },
      {
        id: 'SubscriptionBilling',
        label: 'Subscription and Billing',
        path: 'subscription-billing',
        element: <SubsAndBilling />,
      },
      {
        id: 'organisation',
        label: 'organisation',
        path: 'organisation',
        element: <Organisation />,
      },
      {
        id: 'Contributors',
        label: 'Contributors',
        path: 'contributors',
        element: <Contributors />,
      },
      {
        id: 'Feedback',
        label: 'Feedback',
        path: 'feedback',
        element: <Feedback />,
      },
      {
        id: 'Notification',
        label: 'Notification',
        path: 'notification',
        element: <Notification />,
      },
      {
        id: 'Survey',
        label: 'Survey',
        path: 'survey',
        element: <Survey />,
      },
      {
        id: 'CustomMetrics',
        label: 'Custom Metrics',
        path: 'custom-metrics',
        element: <CustomMetrics />,
      },
      {
        id: 'SDLCMap',
        label: 'SDLC Map',
        path: 'sdlc-map',
        element: <SDLCMappings />,
      },
      {
        id: 'ActiveOrganizations',
        label: 'Active Organizations',
        path: 'active-organizations',
        element: <ActiveOrganizations />,
      },
      {
        id: 'NotFound',
        path: '*',
        label: '',
        icon: '',
        element: <NotFound />,
      },
    ];
  }, []);

  return (
    <Routes key={location.pathname}>
      <Route
        path="/"
        element={<Navigate to={`` + routes[0]?.path} replace />}
      />
      {routes.map((route, index) => (
        <Route
          key={index}
          path={`/` + route.path}
          element={
            getUserId() === 'a867b5e7-ee22-4a9f-becd-b8f3aa43f753' ||
            getRole() === UserRole.EMPLOYEE ||
            disableRoute(route) ? (
              <Center height="100%">
                <Card>
                  <Stack spacing={4} mb={2}>
                    <Center>
                      <LockIcon height={'80px'} width="300px" />
                    </Center>

                    <Text
                      fontFamily="heading"
                      fontSize="xl"
                      fontWeight="medium"
                      color="text.secondary"
                      alignSelf="center"
                    >
                      You don't have access.
                    </Text>
                  </Stack>
                </Card>
              </Center>
            ) : (
              route.element
            )
          }
        />
      ))}
      <Route path="/integrations/:id" element={<IntegrationDetails />} />
      <Route
        path="/integrations/:id/:name"
        element={<IntegrationGitDetails />}
      />
      <Route
        path="/integrations/:id/:name/:id2"
        element={<IntegrationGitDetails />}
      />
      <Route path="/teams/add-team" element={<AddTeams />} />
      <Route path="/teams/:teamId" element={<AddTeams />} />
      <Route path="/custom-metrics/add" element={<AddMetric />} />
      <Route path="/custom-metrics/edit" element={<AddMetric />} />
      <Route path="/survey/create" element={<SurveyBuilder />} />
      <Route path="/survey/edit/:surveyId" element={<SurveyEditorWrapper />} />
      <Route path="/survey/view/:surveyId" element={<SurveyViewWrapper />} />
    </Routes>
  );
}

export default Settings;
